import { Table } from 'antd'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { AssignmentResource } from '@/Containers/AssignmentSection/AssignmentContainer/Resources/AssignmentResource'
import { StatusBadge } from '@/Ship/Components/UI/StatusBadge/StatusBadge'
import { cx } from '@/Ship/Helpers/cssClass'
import { useMobileMode } from '@/Ship/Hooks/useMobileMode'

import { TASK_STATUS_MAP } from '../Contracts/TaskStatus'
import { INVALID_CELL_CLASS } from '../Helpers/cellStyles'
import { canDeleteAssignment, type RequestExecutorPermissions } from '../Helpers/permissions'
import { statusOption } from '../Helpers/statusOption'

import { makeTaskColumns, taskColumnsWidth, type TaskColumn } from './makeTaskColumns'
import { TaskRowMenu, type TaskRowMenuActions } from './TaskRowMenu'

import styles from './RequestFormTemplate.module.css'

const MENU_WIDTH = 36
/** Нижче — картки замість таблиці */
const PHONE_MAX_WIDTH = 767

/* eslint-disable no-unused-vars -- false positive: ESLint core rule flags param names in function-type signatures */
interface TaskTableProps {
    mode: 'view' | 'edit'
    vn: any
    assignments: any[]
    permissions: RequestExecutorPermissions
    invalidAssignments?: Record<string, Set<string>> | undefined
    onFieldChange?: ((uuid: string, field: string, value: any) => void) | undefined
    /** Редагування: uuid завдання, яке зараз видаляється (його меню показує лоадер) */
    busyTaskUuid?: string | null | undefined
    /** Перегляд: триває зміна статусу — лоадер у меню всіх завдань */
    allBusy?: boolean | undefined
    actions: Omit<TaskRowMenuActions, 'openCard'>
}

const openCard = (task: any) =>
    OpenResourceCardTask({ resource: AssignmentResource, primaryFieldValue: task.uuid, mode: 'view' })

const isInvalid = (invalid: Record<string, Set<string>> | undefined, uuid: string, fields?: string[]): boolean =>
    !!fields && fields.some((field) => invalid?.[uuid]?.has(field))

/**
 * Завдання виконавця: на десктопі antd-таблиця з 11 колонок (без прокрутки на 1200),
 * на телефоні — картки «підпис — значення» з того самого опису колонок.
 * Колонки мемоізуються: нова їх ідентичність змушує antd перемонтувати комірки, і інпут
 * у редагуванні губив би фокус після кожного символу.
 */
export const TaskTable = observer(
    ({
        mode,
        vn,
        assignments,
        permissions,
        invalidAssignments,
        onFieldChange,
        busyTaskUuid,
        allBusy,
        actions,
    }: TaskTableProps) => {
        const isPhone = useMobileMode(PHONE_MAX_WIDTH)
        const editable = mode === 'edit' && permissions.canEdit

        const columns = useMemo(
            () => makeTaskColumns({ mode, vn, editable, onFieldChange, openCard }),
            [mode, vn, editable, onFieldChange]
        )

        const menuFor = (record: any) => (
            <TaskRowMenu
                task={record}
                mode={mode}
                canManage={permissions.canManageTasks}
                canDelete={mode === 'edit' && canDeleteAssignment(permissions, record?.status)}
                busy={allBusy === true || busyTaskUuid === record?.uuid}
                actions={{ ...actions, openCard }}
            />
        )

        const rows = assignments.map((a: any) => ({ ...a, key: a.uuid || a.id }))

        if (isPhone) {
            return (
                <div className={cx(styles, 'cards')}>
                    {rows.map((record) => (
                        <div key={record.key} className={cx(styles, 'card')}>
                            <div className={cx(styles, 'cardHead')}>
                                {columns.find((column) => column.key === 'id')?.render(record)}
                                <StatusBadge
                                    option={statusOption(TASK_STATUS_MAP, record.status)}
                                    fallbackLabel={record.status || '—'}
                                />
                                {menuFor(record)}
                            </div>
                            {columns
                                .filter((column) => column.key !== 'id' && column.key !== 'status')
                                .map((column: TaskColumn) => (
                                    <div
                                        key={column.key}
                                        className={`${cx(styles, 'cardRow')} ${
                                            isInvalid(invalidAssignments, record.uuid, column.invalidFields)
                                                ? cx(styles, 'cardRowInvalid')
                                                : ''
                                        }`}
                                    >
                                        <span className={cx(styles, 'cardLabel')}>{column.title}</span>
                                        <span>{column.render(record)}</span>
                                    </div>
                                ))}
                        </div>
                    ))}
                </div>
            )
        }

        const antColumns = [
            ...columns.map((column) => ({
                key: column.key,
                title: column.title,
                width: column.width,
                render: (_: unknown, record: any) => column.render(record),
                onCell: (record: any) =>
                    isInvalid(invalidAssignments, record.uuid, column.invalidFields)
                        ? { className: INVALID_CELL_CLASS }
                        : {},
            })),
            { key: '_menu', title: '', width: MENU_WIDTH, render: (_: unknown, record: any) => menuFor(record) },
        ]

        return (
            <div className={cx(styles, 'table')}>
                <Table
                    columns={antColumns}
                    dataSource={rows}
                    pagination={false}
                    size="small"
                    tableLayout="fixed"
                    scroll={{ x: taskColumnsWidth(columns, MENU_WIDTH) }}
                />
            </div>
        )
    }
)
